<template>
  <AIPopper
    :placeholder="PLACEHOLDERS.AI_ELEMENT_ENHANCER"
    :last-user-prompt="props.lastUserPrompt"
    :callback="enhanceElementAsync"
    button-text="Enhance"
  />
</template>

<script setup>
import requests from '@/vuex/requests.js';
import { computed } from 'vue';
import { useStore } from 'vuex';
import { getYoutubeThumbnail } from '@/utils.js';
import updater from '@/vuex/project/updater.js';
import AIFormatTools from '@/lib/AI/AIFormatTools.js';
import { PLACEHOLDERS } from '@/lib/AI/constants.js';
import { toast } from 'vue-sonner';
import AIPopper from '../AIPopper/AIPopper.vue';

const store = useStore();

const props = defineProps({
  activeElementId: {
    type: String,
    default: '',
    required: true,
  },
  lastUserPrompt: {
    type: String,
    default: '',
    required: true,
  },
});
const workspaceId = computed(() => store.getters['workspace/getId']);
const getOpenProject = computed(() => store.getters['user/projects/getOpen']);
const getElement = computed(
  () => store.getters['project/data/elements/getNode']
);
const getConnection = computed(
  () => store.getters['project/data/connections/getNode']
);
const getAsset = computed(() => store.getters['project/data/assets/getNode']);

const getComponents = computed(
  () => store.getters['project/data/components/getNodes']
);
const getAttributes = computed(
  () => store.getters['project/data/attributes/getNodes']
);
const getConnections = computed(
  () => store.getters['project/data/connections/getConnections']
);

const getMediumImageUrl = computed(
  () => store.getters['project/data/assets/getMediumImageUrl']
);

const activeElement = computed(() => getElement.value(props.activeElementId));
const activeElementCoverUrl = computed(() => {
  const cover = activeElement.value?.assets?.cover;
  if (cover) {
    if (cover?.type === 'youtube') {
      return { type: 'youtube', path: getYoutubeThumbnail(cover.url) };
    }
    if (cover?.type === 'icon') {
      return { type: 'icon', path: '' };
    }
    if (getAsset.value(cover.id)?.type === 'image') {
      return { type: 'image', path: getMediumImageUrl.value(cover?.id) };
    }
    if (getAsset.value(cover.id)?.type === 'template-image') {
      return {
        type: 'template-image',
        path: getMediumImageUrl.value(cover?.id),
      };
    }
  }
  return { type: 'none', path: '' };
});
const findSourceElements = elementId => {
  const sourceElements = [];
  getConnections.value(elementId).forEach(id => {
    const connection = getConnection.value(id);
    if (connection.targetid === elementId) {
      const element = getElement.value(connection.sourceid);
      if (element) {
        sourceElements.push(element);
      }
    }
  });
  return sourceElements;
};

const elementParents = computed(() => {
  if (!props.activeElementId) return null;
  return findSourceElements(props.activeElementId);
});

const refine = (res, relatedComponents) => {
  [res.title, res.content] = AIFormatTools.addParagraphTags([
    res.title,
    res.content,
  ]);

  [res.title, res.content] = AIFormatTools.filterMentions(
    [res.title, res.content],
    relatedComponents,
    'component'
  );

  res.components = res.components.filter(c =>
    relatedComponents.map(component => component.id).includes(c)
  );

  return res;
};

const updateIds = (texts, existingIds = []) => {
  const extractedIds = texts.flatMap(text =>
    AIFormatTools.getComponentMentionIDs(text)
  );
  return [...new Set([...existingIds, ...extractedIds])];
};
const init = input => {
  const elements = [];
  const currentElement = {
    type: 'Current element',
    title: AIFormatTools.removeBoardMentions(activeElement.value.title),
    content: AIFormatTools.removeBoardMentions(activeElement.value.content),
    components: activeElement.value?.components?.map(c => c.component) || [],
  };
  elements.push(currentElement);
  elementParents.value.forEach((parent, index) => {
    const parentElement = {
      index,
      type: 'Parent element',
      title: AIFormatTools.removeBoardMentions(parent.title),
      content: AIFormatTools.removeBoardMentions(parent.content),
      components: parent.components.map(c => c.component) || [],
    };
    elements.push(parentElement);
  });

  let ids = [];
  ids = updateIds([input], ids);
  elements.forEach(element => {
    ids = updateIds(
      [element.title, element.content],
      [...new Set([...ids, ...element.components])]
    );
  });
  const components = [];
  const temp = getComponents.value(ids) || [];
  temp.forEach((component, index) => {
    const attributes = getAttributes.value(component.attributes);
    const attributesObj = AIFormatTools.parseAttributes(attributes);
    components.push({
      index,
      id: component.id,
      name: component.name,
      attributes: attributesObj,
    });
  });
  const { title } = elements[0];
  const { content } = elements[0];
  let prompt = input;
  const hasTexts = !AIFormatTools.isTextEmpty(title, content, input);
  const hasImage =
    !activeElementCoverUrl.value.type === 'none' &&
    !activeElementCoverUrl.value.type === 'icon';
  if (!hasTexts) {
    if (!hasImage) {
      if (elements.length === 1) {
        elements[0].title = 'Start of a journey';
      } else {
        prompt =
          'Try to use the information from the parent elements. If not possible, Start a new Journey';
      }
    } else {
      prompt = 'Examine the given image and start a journey from there';
    }
  } else {
    prompt = AIFormatTools.removeTags(prompt);
  }
  return { elements, components, prompt };
};
async function enhanceElementAsync(input) {
  const hash = getOpenProject.value;
  const { elements, components, prompt } = init(input || '');
  const data = JSON.stringify(
    {
      elements,
      components,
      prompt,
    },
    null,
    2
  );
  const image = JSON.stringify(activeElementCoverUrl.value, null, 2);
  const configurations = {
    workspaceId: workspaceId.value,
    hash,
    image,
    data,
  };
  await requests.workspaces.ai
    .aiElementEnhancer(configurations)
    .then(res => {
      const response = refine(res.data, components);
      if (getElement.value(props.activeElementId)) {
        store.dispatch('project/data/elements/customUpdate', {
          id: props.activeElementId,
          props: {
            title: `${response.title}`,
            content: `${response.content}`,
            autoHeight: true,
            components: [],
          },
          apply: false,
        });
        if (response.components) {
          response.components = response.components.filter(c =>
            components.map(component => component.id).includes(c)
          );
          if (response.components.length !== 0) {
            response.components.forEach(componentId => {
              store.dispatch('project/data/elements/addComponent', {
                id: props.activeElementId,
                componentId,
                index: 0,
                apply: false,
              });
            });
          }
        }
        store.dispatch('project/config/deselectAll', {
          apply: false,
        });
        store.dispatch('project/config/elements/select', {
          id: [props.activeElementId],
          apply: false,
        });
        updater.complete({ apply: true });
      }
    })
    .catch(() => {
      toast.error('Invalid response from AI service');
    });
}
</script>
